export type TemplateSuggestion = {
  token: string
  label: string
  description: string
  group: "input" | "variable" | "loop"
}

export function buildTemplateSuggestions(
  variableNames: string[] = [],
  {
    includeLoopTokens = true,
    startFieldKeys = [],
  }: { includeLoopTokens?: boolean; startFieldKeys?: string[] } = {}
): TemplateSuggestion[] {
  const uniqueVariableNames = Array.from(
    new Set(variableNames.map((name) => name.trim()).filter((name) => name.length > 0))
  )
  const variableSuggestions = uniqueVariableNames.map((name) => ({
    token: `{${name}}`,
    label: name,
    description: "Workflow variable",
    group: "variable" as const,
  }))

  const uniqueStartFieldKeys = Array.from(
    new Set(startFieldKeys.map((key) => key.trim()).filter((key) => key.length > 0))
  )
  const startFieldSuggestions = uniqueStartFieldKeys.map((key) => ({
    token: `{${key}}`,
    label: key,
    description: "Start input field",
    group: "input" as const,
  }))

  const loopSuggestions: TemplateSuggestion[] = includeLoopTokens
    ? [
        {
          token: "{{loop.item}}",
          label: "Loop item",
          description: "Current loop item",
          group: "loop",
        },
        {
          token: "{{loop.index}}",
          label: "Loop index",
          description: "Current loop index",
          group: "loop",
        },
        {
          token: "{{loop.total}}",
          label: "Loop total",
          description: "Loop item count",
          group: "loop",
        },
      ]
    : []

  return [
    {
      token: "{input}",
      label: "Previous output",
      description: "Previous node output",
      group: "input",
    },
    ...startFieldSuggestions,
    ...variableSuggestions,
    ...loopSuggestions,
  ]
}

export function insertTemplateToken(
  value: string,
  token: string,
  selection: { start: number | null; end: number | null }
): { value: string; cursor: number } {
  const start = selection.start ?? value.length
  const end = selection.end ?? start
  const shouldReplaceOpeningBrace = value.slice(Math.max(0, start - 1), start) === "{"
  const replaceStart = shouldReplaceOpeningBrace ? start - 1 : start
  const nextValue = `${value.slice(0, replaceStart)}${token}${value.slice(end)}`

  return {
    value: nextValue,
    cursor: replaceStart + token.length,
  }
}

export function shouldOpenTemplateSuggestions(value: string, cursor: number | null): boolean {
  if (cursor === null || cursor < 1) return false
  return value[cursor - 1] === "{"
}
